import React, { useState, useEffect } from 'react';
import { getRarityColor, getRarityLabel, getRarityGlow } from '../utils/rarityUtils';
import { formatCurrency } from './shared/formatCompact';
import PortraitPicker from './PortraitPicker';
import './PortraitPicker.css';
import { useGame } from '../context/GameContext';

// Legacy name-based portrait map (mirrors GamePage.CHARACTER_PORTRAITS).
// Used as a fallback for characters who haven't picked a portrait via the
// picker yet but still have a hand-assigned image.
const LEGACY_CHARACTER_PORTRAITS = {
  'James': '/images/character_portraits/james_character.png',
  'Tres El Fahad': '/images/character_portraits/tres_character.png',
};
const resolveDisplayPortrait = (character) => {
  if (!character) return null;
  if (character.portrait_url) return character.portrait_url;
  if (LEGACY_CHARACTER_PORTRAITS[character.name]) return LEGACY_CHARACTER_PORTRAITS[character.name];
  return null;
};

// Helper: normalize magic from object {0:{...},1:{...}} to array [{...},{...}]
const normalizeMagic = (magic) => {
  if (!magic) return [];
  if (Array.isArray(magic)) return magic;
  if (typeof magic === 'object') return Object.values(magic);
  return [];
};

// ============================================================
// SEED GRAPH & HELPERS (mirrors server SeedData.js)
// ============================================================
const SEED_GRAPH = {
  Acid:        ['Energy', 'Water', 'Earth'],
  Agility:     ['Enhancing', 'Charisma', 'Strength', 'Vitality', 'Mentality'],
  Air:         ['Energy', 'Cold', 'Electricity', 'Sonic'],
  Chaos:       ['Evil', 'Trickery', 'Dark'],
  Cold:        ['Energy', 'Air', 'Water'],
  Dark:        ['Evil', 'Death', 'Chaos', 'Trickery'],
  Death:       ['Dark', 'Evil', 'Life', 'Enfeebling'],
  Divination:  ['Mentality', 'Light', 'Time'],
  Earth:       ['Energy', 'Acid', 'Fire'],
  Electricity: ['Energy', 'Air', 'Sonic'],
  Energy:      ['Acid', 'Air', 'Cold', 'Earth', 'Electricity', 'Fire', 'Sonic', 'Water'],
  Enfeebling:  ['Death', 'Evil', 'Enhancing'],
  Enhancing:   ['Enfeebling', 'Agility', 'Healing', 'Protection'],
  Evil:        ['Chaos', 'Dark', 'Death', 'Enfeebling'],
  Fire:        ['Energy', 'Earth', 'Sonic'],
  Good:        ['Law', 'Light', 'Life', 'Healing'],
  Healing:     ['Good', 'Life', 'Enhancing', 'Protection'],
  Law:         ['Good', 'Light', 'Protection'],
  Life:        ['Good', 'Healing', 'Death', 'Light'],
  Light:       ['Good', 'Law', 'Life', 'Divination'],
  Charisma:    ['Agility', 'Mentality', 'Trickery'],
  Mentality:   ['Agility', 'Charisma', 'Divination', 'Strength', 'Vitality'],
  Protection:  ['Law', 'Healing', 'Enhancing'],
  Sonic:       ['Energy', 'Air', 'Electricity', 'Fire'],
  Strength:    ['Agility', 'Mentality', 'Vitality'],
  Time:        ['Divination', 'Travel', 'Trickery'],
  Travel:      ['Time', 'Trickery'],
  Trickery:    ['Chaos', 'Dark', 'Charisma', 'Time', 'Travel'],
  Vitality:    ['Agility', 'Mentality', 'Strength'],
  Water:       ['Energy', 'Acid', 'Cold']
};

// Class seed set restrictions (mirrors server SeedData.js)
// Base classes can only discover seeds within their web.
const MAGE_SEEDS = new Set(['Energy', 'Acid', 'Air', 'Cold', 'Earth', 'Electricity', 'Fire', 'Sonic', 'Water']);
const CLERIC_SEEDS = new Set(['Good', 'Evil', 'Law', 'Chaos', 'Healing', 'Dark', 'Death', 'Light', 'Life', 'Protection', 'Enfeebling', 'Enhancing', 'Divination', 'Trickery', 'Time', 'Travel']);
const PSION_SEEDS = new Set(['Strength', 'Agility', 'Vitality', 'Mentality', 'Charisma']);
const CLASS_SEED_SETS = { Mage: MAGE_SEEDS, Cleric: CLERIC_SEEDS, Druid: CLERIC_SEEDS, Psion: PSION_SEEDS };

const SEED_COLORS = {
  Energy: '#f1c40f', Fire: '#e74c3c', Water: '#3498db', Earth: '#8B4513',
  Air: '#87CEEB', Cold: '#00CED1', Acid: '#7FFF00', Electricity: '#FFD700',
  Sonic: '#FF8C00', Light: '#FFFACD', Dark: '#4a0e4e',
  Life: '#2ecc71', Good: '#f0e68c', Evil: '#8B0000', Chaos: '#FF4500',
  Law: '#4169E1', Death: '#2c2c2c', Healing: '#98FB98', Protection: '#4682B4',
  Trickery: '#9370DB', Divination: '#DEB887', Enhancing: '#32CD32',
  Enfeebling: '#CD5C5C', Travel: '#20B2AA', Time: '#C0C0C0',
  Strength: '#e74c3c', Agility: '#3498db', Vitality: '#27ae60',
  Mentality: '#9b59b6', Charisma: '#e67e22'
};

const seedLevel = (xp) => Math.floor(Math.sqrt(xp / 100));
const seedXpForLevel = (level) => level * level * 100;

const SPELL_TYPE_INFO = {
  'damage': { label: 'Damage', color: '#e74c3c', icon: '✨' },
  'heal': { label: 'Heal', color: '#27ae60', icon: '✨' },
  'buff_att': { label: 'Buff ATT', color: '#f39c12', icon: '✨' },
  'buff_def': { label: 'Buff DEF', color: '#3498db', icon: '✨' },
  'debuff_def': { label: 'Debuff DEF', color: '#8e44ad', icon: '✨' },
  'debuff_att': { label: 'Debuff ATT', color: '#c0392b', icon: '✨' },
  'debuff_miss': { label: 'Miss Chance', color: '#7f8c8d', icon: '✨' },
  'hp_divide': { label: 'HP Divide', color: '#2c3e50', icon: '✨' },
  'mirror_image': { label: 'Mirror Image', color: '#1abc9c', icon: '✨' },
  'divination': { label: 'Divination', color: '#f1c40f', icon: '✨' },
  'travel': { label: 'Travel', color: '#16a085', icon: '✨' },
  'combo_damage': { label: 'Combo', color: '#e67e22', icon: '✨' },
  'drain': { label: 'Drain', color: '#8e44ad', icon: '✨' },
  'combo_buff': { label: 'Combo Buff', color: '#2ecc71', icon: '✨' },
  'combo_debuff': { label: 'Combo Debuff', color: '#e74c3c', icon: '✨' },
  'stat_up': { label: 'Stat Up', color: '#3498db', icon: '✨' },
  'stat_down': { label: 'Stat Down', color: '#e74c3c', icon: '✨' }
};

// Equipment slot display names and order
const EQUIP_SLOTS = [
  { key: 'weapon', label: '⚔️ Weapon' },
  { key: 'offhand', label: '🗡️ Off-Hand' },
  { key: 'helm', label: '⛑️ Helm' },
  { key: 'breastplate', label: '🛡️ Breastplate' },
  { key: 'pauldrons_left', label: '🦾 Left Pauldron' },
  { key: 'pauldrons_right', label: '🦾 Right Pauldron' },
  { key: 'gloves_left', label: '🧤 Left Glove' },
  { key: 'gloves_right', label: '🧤 Right Glove' },
  { key: 'grieves_left', label: '🥾 Left Grieve' },
  { key: 'grieves_right', label: '🥾 Right Grieve' },
  { key: 'belt', label: '🪖 Belt' },
  { key: 'ring_left', label: '💍 Ring (L)' },
  { key: 'ring_right', label: '💍 Ring (R)' },
  { key: 'backpack', label: '🎒 Backpack' },
];

// Item types that can be equipped
const EQUIPPABLE_TYPES = ['weapon', 'breastplate', 'helm', 'gloves', 'grieves', 'pauldrons', 'belt', 'armor', 'backpack', 'ring', 'shield'];
const USABLE_TYPES = ['restore', 'scroll'];

function getEquipSlot(item) {
  if (['gloves', 'grieves', 'pauldrons'].includes(item.type)) {
    const side = (item.slot && item.slot.includes('right')) ? 'right' : 'left';
    return `${item.type}_${side}`;
  }
  if (item.type === 'armor') return 'breastplate';
  if (item.type === 'ring') return 'ring_left';
  if (item.type === 'shield') return 'offhand';
  return item.type;
}

// ============================================================
// MAIN COMPONENT
// ============================================================
const CharacterSheet = ({ character, onClose, onCharacterUpdate, onTeleport }) => {
  // v679.3: setGameState lets us patch character.portrait_url optimistically on save
  // so the HUD updates immediately, bypassing the 300ms refresh debounce.
  const { setGameState } = useGame();
  const [activeTab, setActiveTab] = useState('stats');
  const [showPortraitPicker, setShowPortraitPicker] = useState(false);
  const [pendingPortrait, setPendingPortrait] = useState(null);
  const [savingPortrait, setSavingPortrait] = useState(false);
  const [busyAction, setBusyAction] = useState(null);
  const [statusMsg, setStatusMsg] = useState('');
  const [allSpells, setAllSpells] = useState(null); // lazy-loaded
  const [spellFilter, setSpellFilter] = useState('all');
  const [hoveredInvIdx, setHoveredInvIdx] = useState(null);
  const [chroniclerBadge, setChroniclerBadge] = useState(null);

  // Load the Chronicler streak badge (only renders if the player has an active streak).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/infinite-quest/status', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data?.state?.badge?.active) setChroniclerBadge(data.state.badge);
      } catch { /* badge is optional */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!character) return null;

  // Parse JSONB fields safely
  const parseJSON = (val, fallback) => {
    if (!val) return fallback;
    if (typeof val !== 'string') return val;
    try { return JSON.parse(val); } catch { return fallback; }
  };

  const inventory = parseJSON(character.inventory, []);
  const equipment = parseJSON(character.equipment, {});
  const equippedSpells = parseJSON(character.equipped_spells, []);
  const seeds = parseJSON(character.seeds, {});

  const calcModifier = (stat) => Math.floor((stat - 10) / 2);
  const modStr = (mod) => mod >= 0 ? `+${mod}` : `${mod}`;
  const currentHp = (character.max_hp || 20) - (character.damage || 0);
  const currentMp = (character.max_mp || 0) - (character.used_mp || 0);
  const displayPortrait = resolveDisplayPortrait(character);

  // Flash a status message
  const flash = (msg) => { setStatusMsg(msg); setTimeout(() => setStatusMsg(''), 3000); };

  // ============================================================
  // API HELPERS
  // ============================================================
  const apiCall = async (url, body) => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      body: JSON.stringify(body)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Request failed');
    return data;
  };

  const handleEquip = async (itemName, slotOverride = null) => {
    if (busyAction) return;
    setBusyAction(itemName);
    try {
      const body = { itemName };
      if (slotOverride) body.slotOverride = slotOverride;
      const data = await apiCall('/api/player/equip', body);
      flash(data.message);
      if (onCharacterUpdate) onCharacterUpdate();
    } catch (err) {
      flash(`Error: ${err.message}`);
    }
    setBusyAction(null);
  };

  const handleUnequip = async (slot) => {
    if (busyAction) return;
    setBusyAction(slot);
    try {
      const data = await apiCall('/api/player/unequip', { slot });
      flash(data.message);
      if (onCharacterUpdate) onCharacterUpdate();
    } catch (err) {
      flash(`Error: ${err.message}`);
    }
    setBusyAction(null);
  };

  const handleUseItem = async (itemName) => {
    if (busyAction) return;
    setBusyAction(itemName);
    try {
      const data = await apiCall('/api/player/use-item', { itemName });
      flash(data.message);
      if (data.teleported && onTeleport) {
        onTeleport();
        return; // panel will close, skip further updates
      }
      if (onCharacterUpdate) onCharacterUpdate();
    } catch (err) {
      flash(`Error: ${err.message}`);
    }
    setBusyAction(null);
  };

  const handleToggleSpell = async (spellName) => {
    const newList = equippedSpells.includes(spellName)
      ? equippedSpells.filter(s => s !== spellName)
      : [...equippedSpells, spellName];
    try {
      await apiCall('/api/player/equipped-spells', { equippedSpells: newList });
      if (onCharacterUpdate) onCharacterUpdate();
    } catch (err) {
      flash(`Error: ${err.message}`);
    }
  };

  const handleEquipAllSpells = async (spellNames) => {
    try {
      await apiCall('/api/player/equipped-spells', { equippedSpells: spellNames });
      if (onCharacterUpdate) onCharacterUpdate();
    } catch (err) {
      flash(`Error: ${err.message}`);
    }
  };

  // ============================================================
  // STATS TAB
  // ============================================================
  const ATTR_INFO = {
    Strength: { icon: '⚔️', desc: 'Bonus melee damage on all physical attacks', detail: 'Adds damage to every melee hit, ability strike, and physical skill. Also boosts Smithing crafting.' },
    Agility:  { icon: '🏃', desc: 'Stealing, hiding, fleeing, evasion (Thief)', detail: 'Improves Steal/Mug success, Hide checks, and Run escape chance. Thieves gain AGLmod% Evasion and AGLmod×2 Sneak Attack bonus.' },
    Vitality: { icon: '❤️', desc: 'Boosts HP gained per level (hit die + VIT mod)', detail: 'VIT modifier (floor((VIT - 10) / 2)) is added to your hit die roll each level. Higher VIT = more HP per level. Also used for Mind Over Matter (HP→MP).' },
    Mentality:{ icon: '🔮', desc: 'MP per level · spell power · MP regen', detail: 'MEN modifier added to MP gained per level. Scales ALL spell damage, heal power, and spell resistance penetration. Regen max(1, MENmod) MP per turn in combat. Also boosts Alchemy crafting.' },
    Charisma: { icon: '✨', desc: 'Pet charm & shop discounts', detail: 'Improves Charm success rate vs monsters. Gives shop buy/sell discounts (modifier × 1.5%). Primary stat for Druids and pet builds.' },
  };

  const COMBAT_INFO = {
    Attack:  { desc: 'Hit bonus (d20 + ATT vs enemy DEF)', detail: 'Added to your d20 attack roll. Higher ATT means more hits land. Comes from class base, weapon, skill tree, and buffs.' },
    Defense: { desc: 'Defense bonus (d20 + DEF vs enemy ATT)', detail: 'Added to your d20 defense roll against monster attacks. Comes from class base, armor, skill tree, and buffs.' },
    DR:      { desc: 'Damage Reduction — flat reduction on all incoming damage', detail: 'Subtracted from every hit you take (minimum 1 damage). Comes from armor, equipment gems, and skill tree.' },
  };

  const renderStatsTab = () => {
    const stats = [
      { label: 'Strength', val: character.strength },
      { label: 'Agility', val: character.agility },
      { label: 'Vitality', val: character.vitality },
      { label: 'Mentality', val: character.mentality },
      { label: 'Charisma', val: character.charisma },
    ];

    return (
      <div className="cs-content">
        <div className="cs-section">
          <h4>Basic Information</h4>
          <div className="cs-stat-grid">
            <div className="cs-stat"><span className="cs-label">Name:</span><span className="cs-val">{character.name}</span></div>
            <div className="cs-stat"><span className="cs-label">Class:</span><span className="cs-val">{character.advanced_class || character.class}</span></div>
            <div className="cs-stat"><span className="cs-label">Race:</span><span className="cs-val">{character.race || 'Human'}</span></div>
            <div className="cs-stat"><span className="cs-label">Level:</span><span className="cs-val">{character.level}</span></div>
            <div className="cs-stat"><span className="cs-label">Experience:</span><span className="cs-val">{formatCurrency(character.experience || 0)}</span></div>
            <div className="cs-stat"><span className="cs-label">Gold:</span><span className="cs-val gold">{formatCurrency(character.gold || 0)} gp</span></div>
            {character.guild_name && (
              <div className="cs-stat"><span className="cs-label">Guild:</span><span className="cs-val">[{character.guild_tag}] {character.guild_name}</span></div>
            )}
          </div>
        </div>

        <div className="cs-section">
          <h4>Vitals</h4>
          <div className="cs-vitals">
            <div className="cs-bar-row">
              <span className="cs-bar-label">HP</span>
              <div className="cs-bar"><div className="cs-bar-fill hp" style={{ width: `${(currentHp / (character.max_hp || 1)) * 100}%` }} /></div>
              <span className="cs-bar-text">{currentHp} / {character.max_hp}</span>
            </div>
            {character.max_mp > 0 && (
              <div className="cs-bar-row">
                <span className="cs-bar-label">MP</span>
                <div className="cs-bar"><div className="cs-bar-fill mp" style={{ width: `${(currentMp / (character.max_mp || 1)) * 100}%` }} /></div>
                <span className="cs-bar-text">{currentMp} / {character.max_mp}</span>
              </div>
            )}
          </div>
        </div>

        <div className="cs-section">
          <h4>Attributes <span className="cs-hint">(hover for details)</span></h4>
          <div className="cs-attr-list">
            {stats.map(s => {
              const mod = calcModifier(s.val || 10);
              const info = ATTR_INFO[s.label];
              return (
                <div key={s.label} className="cs-attr-row">
                  <div className="cs-attr-main">
                    <span className="cs-attr-icon">{info.icon}</span>
                    <span className="cs-label">{s.label}:</span>
                    <span className="cs-val">{s.val || 10} <span className="cs-mod">({modStr(mod)})</span></span>
                  </div>
                  <div className="cs-attr-desc">{info.desc}</div>
                  <div className="cs-attr-tooltip">{info.detail}</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="cs-section">
          <h4>Combat <span className="cs-hint">(hover for details)</span></h4>
          <div className="cs-attr-list">
            <div className="cs-attr-row">
              <div className="cs-attr-main">
                <span className="cs-attr-icon">⚔️</span>
                <span className="cs-label">Attack:</span>
                <span className="cs-val">{character.att || 0}</span>
              </div>
              <div className="cs-attr-desc">{COMBAT_INFO.Attack.desc}</div>
              <div className="cs-attr-tooltip">{COMBAT_INFO.Attack.detail}</div>
            </div>
            <div className="cs-attr-row">
              <div className="cs-attr-main">
                <span className="cs-attr-icon">🛡️</span>
                <span className="cs-label">Defense:</span>
                <span className="cs-val">{character.def || 0}</span>
              </div>
              <div className="cs-attr-desc">{COMBAT_INFO.Defense.desc}</div>
              <div className="cs-attr-tooltip">{COMBAT_INFO.Defense.detail}</div>
            </div>
            {(character.dr > 0) && (
              <div className="cs-attr-row">
                <div className="cs-attr-main">
                  <span className="cs-attr-icon">🪨</span>
                  <span className="cs-label">DR:</span>
                  <span className="cs-val">{character.dr}</span>
                </div>
                <div className="cs-attr-desc">{COMBAT_INFO.DR.desc}</div>
                <div className="cs-attr-tooltip">{COMBAT_INFO.DR.detail}</div>
              </div>
            )}
            <div className="cs-attr-row">
              <div className="cs-attr-main">
                <span className="cs-attr-icon">🗡️</span>
                <span className="cs-label">Weapon:</span>
                <span className="cs-val">{equipment.weapon ? equipment.weapon.name : 'Fists'}</span>
              </div>
              <div className="cs-attr-desc">
                {equipment.weapon ? `Damage: ${equipment.weapon.damage || '1d4'}${equipment.weapon.att ? ` · ATT +${equipment.weapon.att}` : ''}` : 'Damage: 1d4'}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ============================================================
  // EQUIPMENT & INVENTORY TAB
  // ============================================================
  const renderEquipmentTab = () => {
    // Check if character has the Dual Wield AC ability (Ranger)
    const acAbilities = parseJSON(character.ac_abilities, {});
    const hasDualWield = acAbilities.dual_wield >= 1;
    const visibleSlots = EQUIP_SLOTS.filter(s => s.key !== 'offhand' || hasDualWield || (equipment.offhand && equipment.offhand.type === 'shield')).map(s => {
      if (s.key === 'offhand' && equipment.offhand && equipment.offhand.type === 'shield') {
        return { ...s, label: '🛡️ Shield' };
      }
      return s;
    });

    return (
    <div className="cs-content">
      <div className="cs-section">
        <h4>Equipped Gear</h4>
        <div className="cs-equip-grid">
          {visibleSlots.map(({ key, label }) => {
            const item = equipment[key];
            return (
              <div key={key} className={`cs-equip-slot ${item ? 'filled' : ''}`}>
                <span className="cs-equip-label">{label}</span>
                {item ? (
                  <div className="cs-equip-item-wrap">
                    <div className="cs-equip-item">
                      <span className="cs-equip-name" style={{ color: getRarityColor(item), textShadow: getRarityGlow(item) }}>{item.name}</span>
                      <button
                        className="cs-btn-small danger"
                        onClick={() => handleUnequip(key)}
                        disabled={!!busyAction}
                      >✖</button>
                    </div>
                    <div className="cs-equip-stats">
                      {item.damage && <span>Dmg {item.damage}</span>}
                      {item.att > 0 && <span>ATT +{item.att}</span>}
                      {item.bonus > 0 && <span>DEF +{item.bonus}</span>}
                      {item.dr > 0 && <span>DR {item.dr}</span>}
                      {item.size > 0 && <span>+{item.size} slots</span>}
                      {normalizeMagic(item.magic).filter(m => (m.mod || m.bonus || 0) !== 0).map((m, i) => (
                        <span key={i} style={{ color: '#bf7fff' }}>+{m.mod || m.bonus || 0} {m.type === 'ablup' ? (m.abl || '').toUpperCase() : m.type.toUpperCase()}</span>
                      ))}
                      {item.sockets && item.sockets.length > 0 && (
                        <span style={{ color: '#e91e63' }}>💎 {item.sockets.filter(s => s).length}/{item.sockets.length}</span>
                      )}
                      {item.enchantments && item.enchantments.length > 0 && item.enchantments.map((ench, i) => (
                        <span key={`e${i}`} style={{ color: '#ffb74d' }}>📜 {ench.label}</span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <span className="cs-equip-empty">— empty —</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="cs-section">
        <h4>Inventory ({inventory.length} items)</h4>
        {inventory.length === 0 ? (
          <div className="cs-empty">Your inventory is empty.</div>
        ) : (
          <div className="cs-inv-list">
            {inventory.map((item, idx) => {
              const canEquip = EQUIPPABLE_TYPES.includes(item.type);
              const canUse = USABLE_TYPES.includes(item.type);
              const targetSlot = canEquip ? getEquipSlot(item) : null;
              const canOffhand = item.type === 'weapon' && hasDualWield;
              const rarColor = getRarityColor(item);
              const rarGlow = getRarityGlow(item);
              const rarLabel = getRarityLabel(item);
              const isHovered = hoveredInvIdx === idx;

              // Compute comparison deltas vs equipped item
              const equippedItem = canEquip && targetSlot ? equipment[targetSlot] : null;
              const deltas = [];
              if (equippedItem && equippedItem.name !== item.name) {
                const cmp = (nv, ov, label) => {
                  const a = parseInt(nv) || 0, b = parseInt(ov) || 0;
                  if (a === 0 && b === 0) return;
                  const d = a - b;
                  if (d !== 0) deltas.push({ label, diff: d > 0 ? `+${d}` : `${d}`, color: d > 0 ? '#4caf50' : '#f44336', icon: d > 0 ? '▲' : '▼' });
                };
                cmp(item.att, equippedItem.att, 'Attack');
                cmp(item.bonus, equippedItem.bonus, 'Defense');
                cmp(item.dr, equippedItem.dr, 'Dmg Reduction');
                cmp(item.size, equippedItem.size, 'Capacity');
                const magicSum = (raw) => { const s = {}; normalizeMagic(raw).forEach(m => { s[m.type] = (s[m.type] || 0) + (m.mod || m.bonus || 0); }); return s; };
                const nm = magicSum(item.magic), om = magicSum(equippedItem.magic);
                new Set([...Object.keys(nm), ...Object.keys(om)]).forEach(t => {
                  const d = (nm[t] || 0) - (om[t] || 0);
                  if (d !== 0) deltas.push({ label: t === 'ablup' ? 'STAT' : t.toUpperCase(), diff: d > 0 ? `+${d}` : `${d}`, color: d > 0 ? '#4caf50' : '#f44336', icon: d > 0 ? '▲' : '▼' });
                });
              }

              // Quick stat summary for the row itself
              const statBits = [];
              if (item.damage) statBits.push(`Dmg ${item.damage}`);
              if (item.att) statBits.push(`ATT +${item.att}`);
              if (item.bonus) statBits.push(`DEF +${item.bonus}`);
              if (item.dr) statBits.push(`DR ${item.dr}`);
              if (item.hp) statBits.push(`HP +${item.hp}`);
              if (item.mp) statBits.push(`MP +${item.mp}`);
              if (item.size) statBits.push(`+${item.size} slots`);

              return (
                <div
                  key={`${item.name}-${idx}`}
                  className="cs-inv-item"
                  style={{ borderLeftColor: rarColor }}
                  onMouseEnter={() => setHoveredInvIdx(idx)}
                  onMouseLeave={() => setHoveredInvIdx(null)}
                >
                  <div className="cs-inv-info">
                    <div className="cs-inv-name-row">
                      <span className="cs-inv-name" style={{ color: rarColor, textShadow: rarGlow }}>{item.name}</span>
                      {(item.quantity || 1) > 1 && <span className="cs-inv-qty">x{item.quantity}</span>}
                    </div>
                    <div className="cs-inv-meta-row">
                      <span className="cs-inv-type">{item.type}</span>
                      {rarLabel && rarLabel !== 'Common' && <span className="cs-inv-rarity" style={{ color: rarColor }}>· {rarLabel}</span>}
                      {item.cost > 0 && <span className="cs-inv-sell">· 💰{Math.floor(item.cost / 4)}gp</span>}
                    </div>
                    {statBits.length > 0 && (
                      <div className="cs-inv-stat-row">{statBits.join(' · ')}</div>
                    )}
                    {normalizeMagic(item.magic).filter(m => (m.mod || m.bonus || 0) !== 0).length > 0 && (
                      <div className="cs-inv-magic-row">
                        {normalizeMagic(item.magic).filter(m => (m.mod || m.bonus || 0) !== 0).map((m, i) => (
                          <span key={i}>+{m.mod || m.bonus || 0} {m.type === 'ablup' ? (m.abl || '').toUpperCase() : m.type.toUpperCase()}</span>
                        ))}
                      </div>
                    )}
                    {!canEquip && item.description && item.description !== 'Unknown item' && <span className="cs-inv-desc">{item.description}</span>}
                  </div>
                  <div className="cs-inv-actions">
                    {canEquip && (
                      <button
                        className="cs-btn-small primary"
                        onClick={() => handleEquip(item.name)}
                        disabled={!!busyAction}
                        title={`Equip to ${targetSlot}`}
                      >Equip</button>
                    )}
                    {canOffhand && (
                      <button
                        className="cs-btn-small primary"
                        onClick={() => handleEquip(item.name, 'offhand')}
                        disabled={!!busyAction}
                        title="Equip to Off-Hand"
                        style={{ marginLeft: 4 }}
                      >🗡️ Off-Hand</button>
                    )}
                    {canUse && (
                      <button
                        className="cs-btn-small success"
                        onClick={() => handleUseItem(item.name)}
                        disabled={!!busyAction}
                      >Use</button>
                    )}
                  </div>

                  {/* ── Hover Tooltip ── */}
                  {isHovered && (
                    <div className="cs-inv-tooltip">
                      <div className="cs-tt-name" style={{ color: rarColor, textShadow: rarGlow }}>{item.name}</div>
                      <div className="cs-tt-rarity" style={{ color: rarColor }}>{rarLabel}</div>
                      <div className="cs-tt-type">{(item.type || 'misc').toUpperCase()}</div>
                      {!canEquip && item.description && item.description !== 'Unknown item' && <div className="cs-tt-desc">{item.description}</div>}
                      {item.lore && <div className="cs-tt-lore">{item.lore}</div>}
                      <div className="cs-tt-divider" />

                      {item.quality && item.quality !== 'standard' && (
                        <div className="cs-tt-stat" style={{ color: '#d4a017' }}>Quality: {item.quality}</div>
                      )}
                      {item.enhancement > 0 && (
                        <div className="cs-tt-stat" style={{ color: '#4caf50' }}>Enhanced: +{item.enhancement}</div>
                      )}
                      {item.damage && <div className="cs-tt-stat">Damage: {item.damage}</div>}
                      {item.att > 0 && <div className="cs-tt-stat">Attack: +{item.att}</div>}
                      {item.bonus > 0 && <div className="cs-tt-stat">Defense: +{item.bonus}</div>}
                      {item.dr > 0 && <div className="cs-tt-stat">Dmg Reduction: {item.dr}</div>}
                      {item.mtype && <div className="cs-tt-stat">Melee Type: {item.mtype}</div>}
                      {item.size > 0 && <div className="cs-tt-stat" style={{ color: '#d4a017' }}>Capacity: +{item.size} slots</div>}
                      {item.mdamage && item.mtype && (
                        <div className="cs-tt-stat" style={{ color: '#ff9800' }}>{item.mtype} damage: {item.mdamage}</div>
                      )}
                      {item.sockets && item.sockets.length > 0 && (
                        <div className="cs-tt-stat" style={{ color: '#e91e63' }}>Gems: {item.sockets.filter(s => s).length}/{item.sockets.length} socketed</div>
                      )}
                      {item.enchantments && item.enchantments.length > 0 && item.enchantments.map((ench, ei) => (
                        <div key={ei} className="cs-tt-stat" style={{ color: '#ffb74d' }}>📜 {ench.label}</div>
                      ))}
                      {item.hp > 0 && <div className="cs-tt-stat" style={{ color: '#e74c3c' }}>Restores {item.hp} HP</div>}
                      {item.mp > 0 && <div className="cs-tt-stat" style={{ color: '#3498db' }}>Restores {item.mp} MP</div>}
                      {item.spell && <div className="cs-tt-stat" style={{ color: '#f39c12' }}>Teaches: {item.spell}</div>}
                      {normalizeMagic(item.magic).filter(m => (m.mod || m.bonus || 0) !== 0).length > 0 && (
                        <div className="cs-tt-magic">
                          {normalizeMagic(item.magic).filter(m => (m.mod || m.bonus || 0) !== 0).map((m, mi) => (
                            <div key={mi} className="cs-tt-stat" style={{ color: '#bf7fff' }}>
                              +{m.mod || m.bonus || 0} {m.type === 'ablup' ? (m.abl || '').toUpperCase() : m.type.toUpperCase()}
                            </div>
                          ))}
                        </div>
                      )}
                      {item.level > 1 && <div className="cs-tt-stat" style={{ color: '#c66' }}>Requires Lv {item.level}</div>}
                      {item.type === 'shield' && (
                        <div className="cs-tt-stat" style={{ color: '#ff9800', fontStyle: 'italic' }}>⚠ Non-Fighter: -3 ATT penalty</div>
                      )}
                      <div className="cs-tt-value">{item.cost || 0} gp (sell: {Math.floor((item.cost || 0) / 4)} gp)</div>
                      {(item.quantity || 1) > 1 && <div className="cs-tt-qty">Quantity: {item.quantity}</div>}

                      {/* Comparison vs equipped */}
                      {deltas.length > 0 && (
                        <div className="cs-tt-compare">
                          <div className="cs-tt-divider" />
                          <div className="cs-tt-compare-header">vs equipped: {equippedItem.name}</div>
                          {deltas.map((d, di) => (
                            <div key={di} className="cs-tt-delta" style={{ color: d.color }}>
                              {d.icon} {d.label}: {d.diff}
                            </div>
                          ))}
                        </div>
                      )}
                      {canEquip && !equippedItem && (
                        <div className="cs-tt-compare">
                          <div className="cs-tt-divider" />
                          <div className="cs-tt-compare-header" style={{ color: '#4caf50' }}>Nothing equipped in this slot</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
  };

  // ============================================================
  // MAGIC & SPELLS TAB
  // ============================================================
  const renderMagicTab = () => {
    // Compute available spells from seed levels (client-side mirror of server logic)
    // We'll use a simplified check: if character has the seed, they can cast spells requiring it
    const knownSeeds = Object.entries(seeds).filter(([, xp]) => xp > 0);

    if (knownSeeds.length === 0 && (!character.class || !['Mage', 'Cleric', 'Psion', 'Druid'].includes(character.class))) {
      return (
        <div className="cs-content">
          <div className="cs-empty">
            <p>No magic seeds learned yet.</p>
            <p style={{ color: '#888', fontSize: '0.85em' }}>Seeds are gained through combat and grant access to spells.</p>
          </div>
        </div>
      );
    }

    // Group equipped spell count
    const equippedCount = equippedSpells.length;

    // Spell list from server (we could compute but it's complex - show seeds + equipped spells)
    return (
      <div className="cs-content">
        <div className="cs-section">
          <h4>🧙 Combat Spell Loadout</h4>
          <p style={{ color: '#999', fontSize: '0.85em', margin: '0 0 0.75rem' }}>
            Toggle spells ON/OFF for combat. Only equipped spells appear in battle.
            {equippedCount === 0 && ' (All spells available if none selected)'}
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
            <button className="cs-btn-small primary" onClick={() => handleEquipAllSpells([])}>
              Reset (Show All)
            </button>
          </div>

          {equippedCount > 0 ? (
            <div className="cs-spell-equipped-list">
              {equippedSpells.map(name => (
                <div key={name} className="cs-spell-tag equipped" onClick={() => handleToggleSpell(name)}>
                   {name} <span className="cs-spell-remove"></span>
                </div>
              ))}
            </div>
          ) : (
            <div className="cs-empty" style={{ padding: '0.5rem' }}>All known spells active in combat.</div>
          )}
        </div>

        <div className="cs-section">
          <h4>🌱 Known Seeds / Spells</h4>
          <p style={{ color: '#999', fontSize: '0.85em', margin: '0 0 0.75rem' }}>
            Your seed levels determine which spells you can cast. Higher seed levels unlock stronger spells.
          </p>
          {knownSeeds.length === 0 ? (
            <div className="cs-empty">No seeds learned yet.</div>
          ) : (
            <div className="cs-seed-spell-list">
              {knownSeeds.map(([seedName, xp]) => {
                const level = seedLevel(xp);
                const color = SEED_COLORS[seedName] || '#888';
                return (
                  <div key={seedName} className="cs-seed-spell-item">
                    <div className="cs-seed-header" style={{ borderLeftColor: color }}>
                      <span className="seed-dot" style={{ background: color }} />
                      <strong>{seedName}</strong>
                      <span style={{ color, marginLeft: 'auto' }}>Lv {level}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="cs-section">
          <h4>✨ Class Abilities</h4>
          <div className="cs-ability-list">
            {getClassAbilities(character.class, character.level).map((ability, idx) => (
              <div key={idx} className="cs-ability-item">
                <span className="cs-ability-name">{ability.name}</span>
                <span className="cs-ability-desc">{ability.desc}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // ============================================================
  // SEEDS TAB (preserved from original)
  // ============================================================
  const renderSeedsTab = () => {
    const knownSeeds = Object.entries(seeds).filter(([, xp]) => xp > 0);
    const learnableSeeds = {};
    const allowedSet = CLASS_SEED_SETS[character.class];

    // Compute AC bonus seeds on client side
    let acBonusSeeds = null;
    if (character.advanced_class && character.ac_secondary) {
      acBonusSeeds = CLASS_SEED_SETS[character.ac_secondary] || null;
      // Special case: Rogue gets custom seed access
      if (character.advanced_class === 'Rogue') {
        acBonusSeeds = new Set(['Trickery', 'Charisma', 'Time', 'Travel', 'Chaos', 'Dark']);
      }
    }

    knownSeeds.forEach(([seedName]) => {
      const adjacent = SEED_GRAPH[seedName] || [];
      adjacent.forEach(adj => {
        if (!seeds[adj] || seeds[adj] <= 0) {
          // Class restriction: only show seeds in this class's web or AC bonus web
          const inBaseWeb = allowedSet && allowedSet.has(adj);
          const inACWeb = acBonusSeeds && acBonusSeeds.has(adj);
          if (allowedSet && !inBaseWeb && !inACWeb) return;
          if (!learnableSeeds[adj]) learnableSeeds[adj] = [];
          learnableSeeds[adj].push(seedName);
        }
      });
    });

    return (
      <div className="cs-content">
        <div className="cs-section">
          <h4>🌱 Known Seeds</h4>
          {knownSeeds.length === 0 ? (
            <div className="cs-empty">No seeds learned yet. Seeds are gained through combat.</div>
          ) : (
            <div className="cs-seeds-grid">
              {knownSeeds.map(([seedName, xp]) => {
                const level = seedLevel(xp);
                const nextLevelXp = seedXpForLevel(level + 1);
                const currentLevelXp = seedXpForLevel(level);
                const progressPct = nextLevelXp > currentLevelXp
                  ? ((xp - currentLevelXp) / (nextLevelXp - currentLevelXp)) * 100
                  : 100;
                const color = SEED_COLORS[seedName] || '#888';

                return (
                  <div key={seedName} className="cs-seed-card" style={{ borderColor: color }}>
                    <div className="cs-seed-card-header">
                      <span className="seed-dot" style={{ background: color }} />
                      <span className="cs-seed-name">{seedName}</span>
                      <span className="cs-seed-level" style={{ color }}>Lv {level}</span>
                    </div>
                    <div className="cs-seed-bar-wrap">
                      <div className="cs-seed-bar">
                        <div className="cs-seed-bar-fill" style={{ width: `${Math.min(100, progressPct)}%`, background: color }} />
                      </div>
                      <div className="cs-seed-xp-text">{xp} / {nextLevelXp} XP</div>
                    </div>
                    {SEED_GRAPH[seedName] && (
                      <div className="cs-seed-connections">
                        Connects: {SEED_GRAPH[seedName].map(adj => (
                          <span key={adj} className="cs-conn-tag" style={{
                            borderColor: seeds[adj] > 0 ? (SEED_COLORS[adj] || '#888') : '#555',
                            color: seeds[adj] > 0 ? (SEED_COLORS[adj] || '#888') : '#777',
                            opacity: seeds[adj] > 0 ? 1 : 0.6
                          }}>
                            {adj}{seeds[adj] > 0 ? ` (${seedLevel(seeds[adj])})` : ''}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {Object.keys(learnableSeeds).length > 0 && (
          <div className="cs-section">
            <h4>🌿 Learnable Seeds</h4>
            <p style={{ color: '#999', fontSize: '0.85em', marginBottom: '0.5rem' }}>
              Adjacent to your known seeds - can be learned through gameplay.
            </p>
            <div className="cs-learnable-list">
              {Object.entries(learnableSeeds).map(([seedName, fromSeeds]) => (
                <div key={seedName} className="cs-learnable-item">
                  <span className="seed-dot" style={{ background: SEED_COLORS[seedName] || '#888' }} />
                  <span className="cs-learnable-name">{seedName}</span>
                  <span className="cs-learnable-from">via {fromSeeds.join(', ')}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };









  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="cs-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="cs-sheet">
        <div className="cs-header">
          <h2>📜 Character Sheet</h2>
          <button className="cs-close" onClick={onClose}>✖</button>
        </div>

        <div className="cs-identity">
          <button
            type="button"
            className={`cs-portrait-frame ${displayPortrait ? 'has-portrait' : 'no-portrait'}`}
            onClick={() => { setPendingPortrait(character.portrait_url || null); setShowPortraitPicker(true); }}
            title="Click to change portrait"
            aria-label="Change portrait"
          >
            {displayPortrait ? (
              <img src={displayPortrait} alt={`${character.name} portrait`} />
            ) : (
              <div className="cs-portrait-placeholder">
                <div className="cs-portrait-placeholder-icon">👤</div>
                <div className="cs-portrait-placeholder-text">Choose Portrait</div>
              </div>
            )}
            <div className="cs-portrait-overlay">
              <span className="cs-portrait-overlay-icon">🖼️</span>
              <span className="cs-portrait-overlay-text">Change Portrait</span>
            </div>
          </button>
          <div className="cs-identity-info">
            <div className="cs-identity-name">{character.name}</div>
            <div className="cs-identity-subtitle">
              Level {character.level} {character.race || 'Human'} {character.advanced_class || character.class}
            </div>
            {chroniclerBadge && (
              <div
                className="cs-chronicler-badge"
                title={`${chroniclerBadge.tierName} — ${chroniclerBadge.days}-day streak · +${chroniclerBadge.discountPct}% Crown Shop discount`}
              >
                📜 {chroniclerBadge.tierName} · Tier {chroniclerBadge.level}/{chroniclerBadge.maxLevel}
              </div>
            )}
            {character.guild_name && (
              <div className="cs-identity-guild">[{character.guild_tag}] {character.guild_name}</div>
            )}
          </div>
        </div>

        {statusMsg && <div className="cs-status-bar">{statusMsg}</div>}

        <div className="cs-tabs">
          {[
            { key: 'stats', label: '📊 Stats' },
            { key: 'equipment', label: '⚔️ Equipment' },
            { key: 'magic', label: '🔮 Magic' },
            { key: 'seeds', label: '🌱 Seeds' },
          ].map(tab => (
            <button
              key={tab.key}
              className={`cs-tab ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'stats' && renderStatsTab()}
        {activeTab === 'equipment' && renderEquipmentTab()}
        {activeTab === 'magic' && renderMagicTab()}
        {activeTab === 'seeds' && renderSeedsTab()}
      </div>

      {showPortraitPicker && (
        <div className="pp-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowPortraitPicker(false); }}>
          <div className="pp-modal">
            <div className="pp-modal-header">
              <h3>Choose Portrait</h3>
              <button className="pp-modal-close" onClick={() => setShowPortraitPicker(false)} aria-label="Close">×</button>
            </div>
            <div className="pp-modal-body">
              <PortraitPicker
                race={character.race || 'Human'}
                selected={pendingPortrait}
                onSelect={setPendingPortrait}
                showClear
              />
            </div>
            <div className="pp-modal-footer">
              <button type="button" className="pp-btn-cancel" onClick={() => setShowPortraitPicker(false)} disabled={savingPortrait}>Cancel</button>
              <button
                type="button"
                disabled={savingPortrait || pendingPortrait === (character.portrait_url || null)}
                onClick={async () => {
                  setSavingPortrait(true);
                  try {
                    const res = await fetch('/api/portraits/me', {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                      body: JSON.stringify({ portrait_url: pendingPortrait })
                    });
                    const data = await res.json();
                    if (!res.ok) throw new Error(data.error || 'Failed to save');
                    // v679.3: patch gameState.character immediately so HUD reflects
                    // the new portrait without waiting for refreshCharacterData's
                    // 300ms debounce or any in-flight fetch guard.
                    if (setGameState) {
                      setGameState(prev => prev.character
                        ? { ...prev, character: { ...prev.character, portrait_url: data.portrait_url ?? null } }
                        : prev);
                    }
                    flash('Portrait updated.');
                    setShowPortraitPicker(false);
                    if (onCharacterUpdate) onCharacterUpdate();
                  } catch (err) {
                    flash(`Error: ${err.message}`);
                  }
                  setSavingPortrait(false);
                }}
              >{savingPortrait ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .cs-overlay {
          position: fixed; top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0,0,0,0.85); display: flex; align-items: center;
          justify-content: center; z-index: 1000;
        }
        .cs-sheet {
          background: #1a1a1a; border: 1px solid #444; border-radius: 8px;
          width: 92%; max-width: 680px; max-height: 85vh; overflow: hidden;
          display: flex; flex-direction: column;
        }
        .cs-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: 0.75rem 1rem; background: #2a2a2a; border-bottom: 1px solid #444;
        }
        .cs-header h2 { margin: 0; color: #839dbf; font-size: 1.1rem; }
        .cs-close {
          background: none; border: none; color: #ccc; font-size: 1.5rem;
          cursor: pointer; width: 30px; height: 30px; display: flex;
          align-items: center; justify-content: center; border-radius: 3px;
        }
        .cs-close:hover { color: #fff; background: #555; }

        .cs-status-bar {
          background: #2a4a2a; color: #8f8; padding: 0.4rem 1rem; font-size: 0.85rem;
          border-bottom: 1px solid #444; text-align: center;
        }

        /* IDENTITY BANNER (portrait + name/class) */
        .cs-identity {
          display: flex; align-items: center; gap: 1rem;
          padding: 0.85rem 1rem; background: linear-gradient(180deg, #20242c 0%, #1a1d24 100%);
          border-bottom: 1px solid #444;
        }
        .cs-portrait-frame {
          position: relative; flex-shrink: 0;
          width: 96px; height: 96px;
          padding: 0; margin: 0;
          background: #0d0d12; border: 2px solid #c9a84c;
          border-radius: 6px; cursor: pointer; overflow: hidden;
          box-shadow: 0 2px 8px rgba(0,0,0,0.6), inset 0 0 0 1px rgba(255,255,255,0.05);
          transition: transform 0.15s, box-shadow 0.15s, border-color 0.15s;
        }
        .cs-portrait-frame:hover {
          border-color: #ffd96b; transform: scale(1.03);
          box-shadow: 0 4px 14px rgba(201,168,76,0.4), inset 0 0 0 1px rgba(255,255,255,0.08);
        }
        .cs-portrait-frame:focus-visible {
          outline: 2px solid #ffd96b; outline-offset: 2px;
        }
        .cs-portrait-frame img {
          width: 100%; height: 100%; object-fit: cover; display: block;
        }
        .cs-portrait-placeholder {
          width: 100%; height: 100%;
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          gap: 0.25rem; color: #888;
          background: repeating-linear-gradient(45deg, #1a1a22, #1a1a22 6px, #15151c 6px, #15151c 12px);
        }
        .cs-portrait-placeholder-icon { font-size: 2rem; opacity: 0.7; }
        .cs-portrait-placeholder-text {
          font-size: 0.7rem; color: #c9a84c; font-weight: bold;
          text-transform: uppercase; letter-spacing: 0.5px;
        }
        .cs-portrait-overlay {
          position: absolute; left: 0; right: 0; bottom: 0;
          background: linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.85) 60%);
          color: #ffd96b; padding: 0.35rem 0.3rem 0.3rem;
          display: flex; flex-direction: column; align-items: center; gap: 1px;
          opacity: 0; transition: opacity 0.15s;
          pointer-events: none;
        }
        .cs-portrait-frame:hover .cs-portrait-overlay,
        .cs-portrait-frame:focus-visible .cs-portrait-overlay {
          opacity: 1;
        }
        .cs-portrait-frame.no-portrait .cs-portrait-overlay {
          /* Hide hover overlay when placeholder already prompts user */
          display: none;
        }
        .cs-portrait-overlay-icon { font-size: 0.9rem; }
        .cs-portrait-overlay-text {
          font-size: 0.65rem; font-weight: bold;
          text-transform: uppercase; letter-spacing: 0.5px;
        }
        .cs-identity-info {
          display: flex; flex-direction: column; gap: 0.2rem; min-width: 0; flex: 1;
        }
        .cs-identity-name {
          color: #ffd96b; font-size: 1.25rem; font-weight: bold;
          text-shadow: 0 1px 2px rgba(0,0,0,0.6);
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .cs-chronicler-badge {
          display: inline-flex; align-items: center; gap: 4px;
          align-self: flex-start;
          font-size: 0.72rem; font-weight: 600;
          padding: 2px 8px; margin-top: 2px;
          color: #e8c170; background: #2a2008;
          border: 1px solid #6a5320; border-radius: 10px;
          cursor: default;
        }
        .cs-identity-subtitle {
          color: #b8c3d4; font-size: 0.85rem;
        }
        .cs-identity-guild {
          color: #9ab; font-size: 0.78rem; font-style: italic;
        }

        @media (max-width: 480px) {
          .cs-identity { padding: 0.6rem 0.7rem; gap: 0.7rem; }
          .cs-portrait-frame { width: 72px; height: 72px; }
          .cs-identity-name { font-size: 1.05rem; }
          .cs-identity-subtitle { font-size: 0.78rem; }
        }

        .cs-tabs {
          display: flex; background: #222; border-bottom: 1px solid #444;
          flex-wrap: wrap;
        }
        .cs-tab {
          flex: 1; padding: 0.6rem 0.5rem; background: transparent; color: #aaa;
          border: none; cursor: pointer; font-size: 0.85rem; transition: all 0.15s;
        }
        .cs-tab:hover { background: #333; color: #ccc; }
        .cs-tab.active { background: #1a1a1a; color: #839dbf; font-weight: bold; }

        .cs-content { flex: 1; overflow-y: auto; padding: 0.75rem; }
        .cs-section { margin-bottom: 1.25rem; }
        .cs-section h4 {
          margin: 0 0 0.6rem; color: #839dbf; font-size: 0.95rem;
          border-bottom: 1px solid #333; padding-bottom: 0.25rem;
        }
        .cs-empty { color: #777; font-style: italic; text-align: center; padding: 1.5rem; }

        /* STATS */
        .cs-stat-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 0.4rem; }
        .cs-stat { display: flex; justify-content: space-between; padding: 0.2rem 0; }
        .cs-label { color: #aaa; }
        .cs-val { color: #eee; font-weight: bold; }
        .cs-val.gold { color: #f1c40f; }
        .cs-mod { color: #888; font-size: 0.85rem; font-weight: normal; }
        .cs-hint { color: #666; font-size: 0.75rem; font-weight: normal; }

        /* ATTRIBUTE ROWS WITH TOOLTIPS */
        .cs-attr-list { display: flex; flex-direction: column; gap: 0.25rem; }
        .cs-attr-row {
          position: relative; padding: 0.4rem 0.6rem; background: #252525;
          border-radius: 4px; border: 1px solid #333; cursor: default;
          transition: border-color 0.15s;
        }
        .cs-attr-row:hover { border-color: #556; background: #2a2a2e; }
        .cs-attr-main { display: flex; align-items: center; gap: 0.5rem; }
        .cs-attr-icon { font-size: 1rem; width: 1.3rem; text-align: center; flex-shrink: 0; }
        .cs-attr-desc { font-size: 0.75rem; color: #888; margin-top: 0.15rem; padding-left: 1.8rem; }
        .cs-attr-tooltip {
          display: none; position: absolute; left: 0; right: 0; top: 100%;
          z-index: 100; background: #1a1a2a; border: 1px solid #667;
          border-radius: 4px; padding: 0.5rem 0.7rem; font-size: 0.78rem;
          color: #ccd; line-height: 1.4; box-shadow: 0 4px 12px rgba(0,0,0,0.5);
          margin-top: 2px;
        }
        .cs-attr-row:hover .cs-attr-tooltip { display: block; }

        /* VITALS BARS */
        .cs-vitals { display: flex; flex-direction: column; gap: 0.4rem; }
        .cs-bar-row { display: flex; align-items: center; gap: 0.5rem; }
        .cs-bar-label { width: 24px; color: #aaa; font-weight: bold; font-size: 0.8rem; text-align: right; }
        .cs-bar { flex: 1; height: 16px; background: #333; border-radius: 3px; overflow: hidden; }
        .cs-bar-fill { height: 100%; border-radius: 3px; transition: width 0.3s; }
        .cs-bar-fill.hp { background: linear-gradient(90deg, #c0392b, #e74c3c); }
        .cs-bar-fill.mp { background: linear-gradient(90deg, #2980b9, #3498db); }
        .cs-bar-text { width: 80px; color: #ccc; font-size: 0.8rem; text-align: right; }

        /* EQUIPMENT */
        .cs-equip-grid { display: flex; flex-direction: column; gap: 0.35rem; }
        .cs-equip-slot {
          display: flex; justify-content: space-between; align-items: flex-start;
          padding: 0.4rem 0.6rem; background: #252525; border-radius: 4px;
          border: 1px solid #333;
        }
        .cs-equip-slot.filled { border-color: #555; background: #2a2a2a; }
        .cs-equip-label { color: #999; font-size: 0.85rem; min-width: 120px; padding-top: 2px; }
        .cs-equip-item-wrap { display: flex; flex-direction: column; flex: 1; min-width: 0; }
        .cs-equip-item { display: flex; align-items: center; gap: 0.5rem; }
        .cs-equip-name { font-weight: bold; font-size: 0.9rem; }
        .cs-equip-stats {
          display: flex; flex-wrap: wrap; gap: 0.25rem 0.6rem;
          font-size: 0.73rem; color: #9ab; padding-left: 0; margin-top: 2px;
        }
        .cs-equip-empty { color: #555; font-style: italic; font-size: 0.85rem; }

        /* INVENTORY */
        .cs-inv-list { display: flex; flex-direction: column; gap: 0.3rem; }
        .cs-inv-item {
          position: relative;
          display: flex; justify-content: space-between; align-items: center;
          padding: 0.4rem 0.6rem; background: #252525; border-radius: 4px;
          border: 1px solid #333; border-left: 3px solid #555;
        }
        .cs-inv-info { display: flex; flex-direction: column; gap: 0.1rem; flex: 1; min-width: 0; }
        .cs-inv-name-row { display: flex; align-items: center; gap: 0.4rem; }
        .cs-inv-name { font-weight: bold; font-size: 0.9rem; }
        .cs-inv-qty { color: #f1c40f; font-size: 0.8rem; }
        .cs-inv-meta-row { display: flex; align-items: center; gap: 0.3rem; font-size: 0.73rem; }
        .cs-inv-type { color: #888; text-transform: uppercase; }
        .cs-inv-rarity { font-size: 0.73rem; }
        .cs-inv-sell { color: #c9a84c; font-size: 0.73rem; }
        .cs-inv-stat-row { font-size: 0.75rem; color: #9ab; }
        .cs-inv-magic-row { display: flex; flex-wrap: wrap; gap: 0.4rem; font-size: 0.73rem; color: #bf7fff; }
        .cs-inv-desc { color: #777; font-size: 0.75rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .cs-inv-actions { display: flex; gap: 0.3rem; flex-shrink: 0; margin-left: 0.5rem; }

        /* INVENTORY ITEM TOOLTIP */
        .cs-inv-tooltip {
          position: absolute; left: 0; right: 0; top: 100%; z-index: 200;
          background: #12121a; border: 1px solid #556; border-radius: 6px;
          padding: 0.6rem 0.75rem; margin-top: 2px;
          box-shadow: 0 6px 24px rgba(0,0,0,0.7);
          font-size: 0.82rem; line-height: 1.45; color: #ddd;
          pointer-events: none;
        }
        .cs-tt-name { font-weight: bold; font-size: 0.95rem; margin-bottom: 1px; }
        .cs-tt-rarity { font-size: 0.78rem; margin-bottom: 1px; }
        .cs-tt-type { font-size: 0.72rem; color: #999; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 2px; }
        .cs-tt-desc { font-size: 0.78rem; color: #bbb; font-style: italic; margin: 2px 0; }
        .cs-tt-lore { font-size: 0.75rem; color: #9b9; font-style: italic; margin: 2px 0; }
        .cs-tt-divider { border-top: 1px solid rgba(255,255,255,0.08); margin: 5px 0; }
        .cs-tt-stat { font-size: 0.8rem; color: #ccc; padding: 1px 0; }
        .cs-tt-magic { margin: 2px 0; }
        .cs-tt-value { font-size: 0.78rem; color: #c9a84c; margin-top: 4px; }
        .cs-tt-qty { font-size: 0.75rem; color: #aaa; }
        .cs-tt-compare { margin-top: 2px; }
        .cs-tt-compare-header { font-size: 0.78rem; color: #aab; font-weight: bold; margin-bottom: 2px; }
        .cs-tt-delta { font-size: 0.8rem; font-weight: bold; padding: 1px 0; }

        /* BUTTONS */
        .cs-btn-small {
          padding: 0.2rem 0.5rem; font-size: 0.75rem; border: 1px solid #555;
          border-radius: 3px; cursor: pointer; color: #eee; transition: all 0.15s;
        }
        .cs-btn-small:disabled { opacity: 0.4; cursor: not-allowed; }
        .cs-btn-small.primary { background: #2a5aa5; border-color: #3a6abf; }
        .cs-btn-small.primary:hover:not(:disabled) { background: #3a6abf; }
        .cs-btn-small.success { background: #2a7a2a; border-color: #3a8a3a; }
        .cs-btn-small.success:hover:not(:disabled) { background: #3a8a3a; }
        .cs-btn-small.danger { background: #7a2a2a; border-color: #8a3a3a; }
        .cs-btn-small.danger:hover:not(:disabled) { background: #8a3a3a; }

        /* SPELL LOADOUT */
        .cs-spell-equipped-list { display: flex; flex-wrap: wrap; gap: 0.35rem; }
        .cs-spell-tag {
          padding: 0.25rem 0.6rem; border-radius: 4px; font-size: 0.8rem;
          cursor: pointer; transition: all 0.15s; border: 1px solid #555;
        }
        .cs-spell-tag.equipped { background: #2a4a2a; color: #8f8; border-color: #4a6a4a; }
        .cs-spell-tag.equipped:hover { background: #3a5a3a; }
        .cs-spell-tag .cs-spell-remove { color: #c66; margin-left: 0.3rem; }

        /* SEED SPELL LIST */
        .cs-seed-spell-list { display: flex; flex-direction: column; gap: 0.4rem; }
        .cs-seed-spell-item { }
        .cs-seed-header {
          display: flex; align-items: center; gap: 0.4rem;
          padding: 0.4rem 0.6rem; background: #252525; border-radius: 4px;
          border-left: 3px solid #555; color: #ddd;
        }

        /* CLASS ABILITIES */
        .cs-ability-list { display: flex; flex-direction: column; gap: 0.35rem; }
        .cs-ability-item {
          padding: 0.4rem 0.6rem; background: #252525; border-radius: 4px;
          border-left: 3px solid #839dbf;
        }
        .cs-ability-name { color: #839dbf; font-weight: bold; font-size: 0.9rem; }
        .cs-ability-desc { color: #999; font-size: 0.8rem; display: block; margin-top: 0.15rem; }

        /* SEEDS TAB */
        .cs-seeds-grid { display: flex; flex-direction: column; gap: 0.75rem; }
        .cs-seed-card {
          background: #252525; border: 2px solid #555; border-radius: 6px; padding: 0.6rem;
        }
        .cs-seed-card-header { display: flex; align-items: center; gap: 0.4rem; margin-bottom: 0.4rem; }
        .seed-dot { width: 12px; height: 12px; border-radius: 50%; flex-shrink: 0; }
        .cs-seed-name { font-weight: bold; color: #e0e0e0; flex: 1; }
        .cs-seed-level { font-weight: bold; font-size: 0.95rem; }
        .cs-seed-bar-wrap { margin-bottom: 0.3rem; }
        .cs-seed-bar { width: 100%; height: 8px; background: #444; border-radius: 4px; overflow: hidden; }
        .cs-seed-bar-fill { height: 100%; border-radius: 4px; transition: width 0.3s; }
        .cs-seed-xp-text { font-size: 0.75rem; color: #aaa; margin-top: 2px; }
        .cs-seed-connections { font-size: 0.75rem; color: #888; margin-top: 0.2rem; }
        .cs-conn-tag {
          display: inline-block; border: 1px solid #555; padding: 1px 5px;
          border-radius: 3px; margin: 2px; font-size: 0.7rem;
        }
        .cs-learnable-list { display: flex; flex-direction: column; gap: 0.35rem; }
        .cs-learnable-item {
          display: flex; align-items: center; gap: 0.4rem;
          padding: 0.35rem 0.5rem; background: #252525; border-radius: 4px;
          border: 1px dashed #555;
        }
        .cs-learnable-name { font-weight: bold; color: #ccc; }
        .cs-learnable-from { color: #888; font-size: 0.8rem; margin-left: auto; }

        /* AC Tab ability rows */
        .cs-ability-header-row {
          display: flex; justify-content: space-between; align-items: center;
        }
        .cs-ability-rank { font-size: 0.8rem; color: #aaa; }
        .cs-ability-rank.maxed { color: #4ade80; }
        .cs-ability-prereq { font-size: 0.75rem; color: #c66; display: block; margin-top: 2px; }
        .cs-ability-choice-row {
          margin-top: 4px; display: flex; gap: 4px; align-items: center; flex-wrap: wrap;
        }
        .cs-ability-choice-select {
          flex: 1; min-width: 120px; padding: 4px 8px; background: #2a2a3a; color: #eee;
          border: 1px solid #555; border-radius: 4px; font-size: 0.8rem;
        }

        /* Mobile responsive for CharacterSheet */
        @media (max-width: 600px) {
          .cs-close { width: 40px; height: 40px; font-size: 1.3rem; }
          .cs-content { padding: 0.5rem; }
          .cs-section h4 { font-size: 0.88rem; }
          .cs-ability-header-row { flex-wrap: wrap; gap: 2px; }
          .cs-ability-rank { font-size: 0.75rem; }
          .cs-ability-choice-row { flex-direction: column; }
          .cs-ability-choice-select { width: 100%; min-width: unset; }
          .cs-equip-slot { flex-wrap: wrap; gap: 4px; }
          .cs-equip-label { min-width: auto; }
          .cs-equip-item-wrap { width: 100%; }
          .cs-bar-text { width: auto; font-size: 0.72rem; }
          .cs-inv-item { flex-wrap: wrap; gap: 4px; }
          .cs-inv-actions { margin-left: 0; width: 100%; justify-content: flex-end; }
          .cs-spell-tag { font-size: 0.75rem; padding: 0.3rem 0.5rem; }
        }
      `}</style>
    </div>
  );
};

// ============================================================
// HELPER: Get class abilities for display
// ============================================================
function getClassAbilities(charClass, charLevel) {
  const abilities = {
    Fighter: [
      { name: 'Boost', level: 1, desc: 'All hits become critical for 2 min. 3hr cooldown.' },
      { name: 'Defensive Stance', level: 3, desc: 'Deal damage + gain DEF bonus for 1 round.' },
      { name: 'Precise Stab', level: 7, desc: '2x damage auto-crit. 10 MP.' },
      { name: 'Recoil Strike', level: 12, desc: '3x damage but take half back.' },
      { name: 'Critical Blow', level: 40, desc: '4x damage auto-crit. 50 MP.' },
      { name: 'Death Strike', level: 80, desc: '8x damage, 10% instant kill. 200 MP.' },
    ],
    Thief: [
      { name: 'Flawless Dodge', level: 1, desc: 'All attacks miss for 2 min. 3hr cooldown.' },
      { name: 'Steal', level: 3, desc: 'Attempt to steal an item from the enemy.' },
      { name: 'Sneak Attack', level: 5, desc: 'Bonus d8 damage dice.' },
      { name: 'Hide', level: 13, desc: 'Become hidden for bonus damage on next attack.' },
      { name: 'Critical Sneak Attack', level: 40, desc: 'Bonus d12 damage dice.' },
      { name: 'Assassin Strike', level: 70, desc: '6x damage, 15% instant kill. 100 MP.' },
      { name: 'Superior Assassin Strike', level: 100, desc: '6x damage, 40% instant kill. 300 MP.' },
    ],
    Mage: [
      { name: 'Mana Fountain', level: 1, desc: 'Spells cost 0 MP for 2 min. 3hr cooldown.' },
      { name: 'Power Cast', level: 10, desc: 'Next spell doubles MEN but costs 3x MP.' },
    ],
    Cleric: [
      { name: 'Miracle', level: 1, desc: 'Full heal + remove all debuffs. 3hr cooldown.' },
      { name: 'Spontaneous Casting', level: 7, desc: 'Next spell is free. 30 min cooldown.' },
    ],
    Psion: [
      { name: 'Third Eye', level: 1, desc: 'Double all damage for 2 min. 3hr cooldown.' },
      { name: 'Mind Over Matter', level: 9, desc: 'Convert HP to MP.' },
    ],
    Druid: [
      { name: 'Charm', level: 1, desc: 'Attempt to charm an enemy as a pet. 6 MP.' },
      { name: 'Friendship', level: 1, desc: 'Force charm (within 5 levels). 3hr cooldown.' },
      { name: 'Dismiss', level: 1, desc: 'Release your current pet.' },
      { name: 'Entangle', level: 12, desc: 'ATT/DEF down + miss% on enemy. 10 MP.' },
      { name: 'Greater Entangle', level: 50, desc: 'Stronger entangle. 50 MP.' },
      { name: 'Perfect Entangle', level: 150, desc: 'Strongest entangle. 250 MP.' },
    ],
  };

  const classAbilities = abilities[charClass] || [];
  return classAbilities.filter(a => (charLevel || 1) >= a.level);
}

export default CharacterSheet;
